import React, { useEffect, useState } from "react";
import { t } from "i18next";

import { NotificationStatus } from "components/Notifications";
import { ReactComponent as EyeIcon } from "assets/icons/commons/Eye.svg";
import { formatDateByLanguage } from "utils/dateFormatter/dateFormatter";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { getAndSetPermanentNotififcations, getCurrentHistoryNotificationById, setHistoryNotificationTableParams } from "redux/notifications/actions";
import type { INotificationsProps } from "./Notifications";
import { Button } from "components/commons";
import { ModalBasic } from "components/commons/Modals";
import { ComponentsLoader } from "components";
import NotificationInfo from "components/Notifications/DropdownNotifications/NotificationInfo";
import NotificationViewActions from "../../components/Task/NotificationViewActions/NotificationViewActions";

const withNotificationsData = (Component: React.FC<INotificationsProps>) => () => {
    const [currentNotificationModalOpen, setCurrentNotificationModalOpen] = useState<boolean>(false);

    const dispatch = useAppDispatch();

    const { historyNotifications, sellerId, language, currentNotification, userId } = useAppSelector(({notifications, sellers, appOptions, users}) => ({
        historyNotifications: notifications.notificationsHistory.table,
        sellerId: sellers.currentSeller.info.data?.id,
        language: appOptions.language.current,
        currentNotification: notifications.notificationsHistory.current,
        userId: users.currentUser.info.id
    }))

    const getHistoryNotificationsList = (params: {[key: string]: number | string}) => {
        dispatch(getAndSetPermanentNotififcations({
            ...params,
            limit: 10,
            locale: language.slice(0,2)
        }) as any)
    }

    useEffect(() => {
        if(sellerId) {
            getHistoryNotificationsList({...historyNotifications.params, seller: sellerId});
        }
    }, [historyNotifications.params, sellerId])

    const changeNotificationPageHandler = (page: number) => {
        dispatch(setHistoryNotificationTableParams({
            ...historyNotifications.params,
            page: page
        }))
    }

    const getCurrentNotificationHanlder = (id: number) => {
        dispatch(getCurrentHistoryNotificationById(id) as any);
        setCurrentNotificationModalOpen(true);
    }  

    const data = React.useMemo(() => {
        return historyNotifications.data?.data?.map((item) => {
            return {
                id: item.id,
                createdAt: formatDateByLanguage(new Date(item.createdAt), "Pp"),
                title: item.title,
                status: <NotificationStatus status={item.status} progress={item.progress} />,
                view: (
                    <Button clickHandler={() => getCurrentNotificationHanlder(item.id)}>
                        <EyeIcon className="stroke-[#7f7f7f] w-[17px] h-[17px]" />
                    </Button>
                )
            }
        })
    }, [historyNotifications.data])

    return (
        <>
            <Component
                isLoading={historyNotifications.isLoading}
                total={historyNotifications.data?.total}
                error={historyNotifications.error}
                notifications={data}
                getParams={historyNotifications.params}
                changeNotificationPageHandler={changeNotificationPageHandler}
            />
            <ModalBasic
                modalOpen={currentNotificationModalOpen}
                setModalOpen={setCurrentNotificationModalOpen}
                id="history-notification-modal"
                title={`${t(`Task status`)} ${currentNotification.data?.id ? currentNotification.data?.id : "..."}`}
            >
                <div className='px-5 py-3 min-h-[82px]'>
                {
                    currentNotification.isLoading ?
                        <ComponentsLoader />
                    : currentNotification.error ?
                        <div className='text-red-500'>{currentNotification.error}</div>
                    : currentNotification.data &&
                        <div>
                            <NotificationInfo
                                currentNotification={currentNotification}
                                result={currentNotification.data.result}
                                error={currentNotification.data.error}
                            />
                            <pre className='dev-only border-t-[1px] max-h-48 mt-3 overflow-auto pt-2 text-xs' style={{display: (userId === 1 ? "block" : "none"), whiteSpace: 'break-spaces'}}>
                                {JSON.stringify(currentNotification.data, undefined, 4)}
                            </pre>
                        </div>
                }
                </div>

                <div className="flex flex-wrap justify-start space-x-2 border-t border-gray-200 px-5 py-3">
                    <NotificationViewActions notification={currentNotification}></NotificationViewActions>
                </div>

            </ModalBasic>
        </>
    )

}

export default withNotificationsData;